<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { formatPrice } from '@/utils/formatPrice';
import { getDateFormat, getDateHourMinute } from '@/utils/formatDate';

const route = useRoute();
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });

const orderId = computed(
  () => (route.params.id as string) ?? (route.query.orderId as string),
);

const { data, isPending, isError } = useQuery({
  queryKey: computed(() => ['order-detail', orderId.value]),
  queryFn: async () => {
    const res = await axiosInstance.get(`orders/get/${orderId.value}`);
    const d = res.data as any;
    return d?.data ?? d;
  },
  enabled: computed(() => !!orderId.value),
});

const order = computed(() => data.value ?? null);

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  pending: { label: t('orders.statusPending'), cls: 'badge--amber' },
  in_progress: { label: t('orders.statusInProgress'), cls: 'badge--blue' },
  ready: { label: t('orders.statusReady'), cls: 'badge--green' },
  served: { label: t('orders.statusServed'), cls: 'badge--green' },
  completed: { label: t('orders.statusCompleted'), cls: 'badge--gray' },
  terminated: { label: t('orders.statusTerminated'), cls: 'badge--gray' },
  cancelled: { label: t('orders.statusCancelled'), cls: 'badge--red' },
  rejected: { label: t('orders.statusRejected'), cls: 'badge--red' },
  failled: { label: t('orders.statusFailed'), cls: 'badge--red' },
};
function statusInfo(s: string) {
  return STATUS_MAP[s] ?? { label: s, cls: 'badge--gray' };
}
function pickupLabel(o: any) {
  if (o.pickupMethod === 'takeaway') return { text: t('orders.takeaway') };
  if (o.pickupMethod === 'dine_in' || o.pickupMethod === 'onsite')
    return { text: t('orders.onsite') };
  if (o.pickupMethod === 'delivery') return { text: t('orders.delivery') };
  return { text: o.pickupMethod ?? '' };
}
function clientName(o: any) {
  const u = o.user;
  if (u?.firstname || u?.lastname)
    return `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim();
  if (o.phone) return o.phone;
  return t('orders.anonymous');
}

function goBack() {
  router.push('/commandes');
}
</script>

<template>
  <div class="od">
    <!-- Breadcrumb -->
    <nav class="od__bc">
      <button class="od__bc-link" @click="goBack">
        {{ t('orders.title') }}
      </button>
      <i class="pi pi-angle-right od__bc-sep" />
      <span class="od__bc-cur">{{
        order?.orderNumber ?? orderId?.slice(0, 8)
      }}</span>
    </nav>

    <div v-if="isPending" class="od__loading">
      <i class="pi pi-spin pi-spinner" />
    </div>
    <div v-else-if="isError || !order" class="od__error">
      <i class="pi pi-exclamation-circle" />
      <p>{{ t('orders.notFound') }}</p>
      <button class="od__btn od__btn--ghost" @click="goBack">
        <i class="pi pi-arrow-left" /> {{ t('orders.title') }}
      </button>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="od__header">
        <button class="od__back" @click="goBack">
          <i class="pi pi-arrow-left" />
        </button>
        <div class="od__header-info">
          <h1 class="od__id">{{ order.orderNumber }}</h1>
          <p class="od__sub">
            {{ getDateFormat(order.createdAt) }} {{ t('common.to') }}
            {{ getDateHourMinute(order.createdAt) }}
          </p>
        </div>
        <span class="od__badge" :class="statusInfo(order.status).cls">
          {{ statusInfo(order.status).label }}
        </span>
      </div>

      <!-- Grille principale -->
      <div class="od__grid">
        <!-- Colonne gauche -->
        <div class="od__col">
          <!-- Info grid -->
          <div class="od__card">
            <div class="od__info-grid">
              <div class="od__cell">
                <span class="od__cell-label">{{
                  t('orders.mode').toUpperCase()
                }}</span>
                <span class="od__cell-val">{{ pickupLabel(order).text }}</span>
              </div>
              <div class="od__cell">
                <span class="od__cell-label">{{
                  t('orders.payment').toUpperCase()
                }}</span>
                <span class="od__cell-val">
                  {{
                    order.paymentMethod === 'online'
                      ? t('orders.payOnline')
                      : order.paymentMethod === 'cash'
                        ? t('orders.payCash')
                        : (order.paymentMethod ?? '—')
                  }}
                </span>
              </div>
              <div v-if="order.orderDate" class="od__cell">
                <span class="od__cell-label">{{
                  t('orders.orderDate').toUpperCase()
                }}</span>
                <span class="od__cell-val"
                  >{{ getDateFormat(order.orderDate) }}
                  {{ getDateHourMinute(order.orderDate) }}</span
                >
              </div>
              <div v-if="order.pickupTime" class="od__cell">
                <span class="od__cell-label">{{
                  t('orders.pickupTime').toUpperCase()
                }}</span>
                <span class="od__cell-val">{{
                  getDateHourMinute(order.pickupTime)
                }}</span>
              </div>
            </div>

            <!-- Reject reason -->
            <div v-if="order.rejectReason" class="od__reject">
              <p class="od__reject-title">{{ t('orders.rejectReason') }}</p>
              <p class="od__reject-msg">{{ order.rejectReason }}</p>
            </div>
          </div>

          <!-- Items -->
          <div class="od__card">
            <div class="od__card-hd">
              <h3 class="od__card-title">
                <i class="pi pi-shopping-bag" />
                {{
                  t('orders.itemsCount', { count: order.items?.length ?? 0 })
                }}
              </h3>
            </div>
            <div class="od__items">
              <div v-for="item in order.items" :key="item.id" class="od-item">
                <div class="od-item__img">
                  <img
                    v-if="item.product?.image?.info?.url"
                    :src="item.product.image.info.url"
                    :alt="item.product.designation"
                    loading="lazy"
                  />
                  <i v-else class="pi pi-image" />
                </div>
                <div class="od-item__body">
                  <p class="od-item__name">
                    {{ item.product?.designation ?? '—' }}
                  </p>
                  <p
                    v-if="item.product?.categories?.[0]?.category?.designation"
                    class="od-item__cat"
                  >
                    {{ item.product.categories[0].category.designation }}
                  </p>
                  <p class="od-item__qty">
                    {{ item.qty }}× · {{ formatPrice(item.unityPrice) }}
                  </p>
                  <p v-if="item.clientNote" class="od-item__note">
                    "{{ item.clientNote }}"
                  </p>
                  <div v-if="item.extras?.length" class="od-item__extras">
                    <div v-for="extra in item.extras" :key="extra?.extra?.id">
                      <p class="od-item__extra-title">
                        {{ extra?.extra?.title }}
                      </p>
                      <div class="od-item__extra-opts">
                        <span
                          v-for="opt in extra.options"
                          :key="opt?.option?.id"
                          class="od-item__opt"
                        >
                          {{ opt.qty > 1 ? opt.qty + '× ' : ''
                          }}{{ opt?.option?.content ?? '?' }}
                          <span
                            v-if="opt?.option?.price > 0"
                            class="od-item__opt-price"
                            >+{{ formatPrice(opt.option.price) }}</span
                          >
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <p class="od-item__total">
                  {{ formatPrice(item.qty * item.unityPrice) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Colonne droite -->
        <div class="od__col">
          <!-- Client -->
          <div class="od__card">
            <div class="od__card-hd">
              <h3 class="od__card-title">
                <i class="pi pi-user" /> {{ t('orders.client') }}
              </h3>
            </div>
            <div class="od__client">
              <div class="od__client-avatar">
                <i class="pi pi-user" />
              </div>
              <div>
                <p class="od__client-name">{{ clientName(order) }}</p>
                <p v-if="order.phone" class="od__client-sub">
                  {{ order.phone }}
                </p>
                <p
                  v-if="order.email ?? order.user?.email"
                  class="od__client-sub"
                >
                  {{ order.email ?? order.user?.email }}
                </p>
              </div>
            </div>

            <!-- Client note -->
            <div v-if="order.clientNote" class="od__note">
              <p class="od__note-label">{{ t('orders.clientNote') }}</p>
              <p class="od__note-text">"{{ order.clientNote }}"</p>
            </div>
          </div>

          <!-- Payments -->
          <div v-if="order.payments?.length" class="od__card">
            <div class="od__card-hd">
              <h3 class="od__card-title">
                <i class="pi pi-wallet" /> {{ t('orders.payment') }}
              </h3>
            </div>
            <div class="od__pay-list">
              <div
                v-for="pay in order.payments"
                :key="pay.id"
                class="od__pay-row"
              >
                <div>
                  <span
                    class="od__pay-chip"
                    :class="
                      pay.status === 'totaly_paid' ? 'pay--green' : 'pay--amber'
                    "
                  >
                    {{
                      pay.status === 'totaly_paid'
                        ? t('orders.paid')
                        : pay.status
                    }}
                  </span>
                  <span v-if="pay.network" class="od__pay-meta">{{
                    pay.network
                  }}</span>
                  <span v-if="pay.cardLastDigit" class="od__pay-meta"
                    >****{{ pay.cardLastDigit }}</span
                  >
                  <p v-if="pay.cur" class="od__pay-cur">{{ pay.cur }}</p>
                </div>
                <span class="od__pay-amount">{{
                  formatPrice(pay.amount)
                }}</span>
              </div>
            </div>
          </div>

          <!-- Totals -->
          <div class="od__card">
            <div class="od__card-hd">
              <h3 class="od__card-title">
                <i class="pi pi-calculator" /> {{ t('orders.total') }}
              </h3>
            </div>
            <div class="od__totals">
              <div class="od__total-row">
                <span>{{ t('orders.subtotal') }}</span
                ><span>{{ formatPrice(order.subTotal) }}</span>
              </div>
              <div class="od__total-row">
                <span>{{ t('orders.vat') }}</span
                ><span>{{ formatPrice(order.taxAmount) }}</span>
              </div>
              <div v-if="order.totalDiscount" class="od__total-row">
                <span>{{ t('orders.discount') }}</span
                ><span class="od__discount"
                  >-{{ formatPrice(order.totalDiscount) }}</span
                >
              </div>
              <div class="od__total-row od__total-row--final">
                <span>{{ t('orders.total') }}</span
                ><span>{{ formatPrice(order.totalPrice) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.od {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .od {
    padding: 1.5rem;
  }
}

.od__bc {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.od__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: inherit;
}
.od__bc-link:hover {
  color: #0b1320;
}
.od__bc-sep {
  font-size: 0.625rem;
}
.od__bc-cur {
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.od__loading,
.od__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.od__error i {
  font-size: 2rem;
  color: var(--nxt-error);
}

.od__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.5rem;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
}
.od__back {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
}
.od__header-info {
  min-width: 0;
  flex: 1;
}
.od__id {
  margin: 0 0 0.15rem;
  font-size: 1.375rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.od__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

.od__badge {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.3rem 0.875rem;
  border-radius: 0.5rem;
}
.badge--amber {
  background: rgba(245, 158, 11, 0.15);
  color: #b45309;
}
.badge--blue {
  background: rgba(59, 130, 246, 0.15);
  color: #1d4ed8;
}
.badge--green {
  background: rgba(16, 185, 129, 0.15);
  color: #047857;
}
.badge--gray {
  background: rgba(148, 163, 184, 0.15);
  color: #475569;
}
.badge--red {
  background: rgba(239, 68, 68, 0.15);
  color: #b91c1c;
}

/* Grille principale */
.od__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
}
@media (max-width: 900px) {
  .od__grid {
    grid-template-columns: 1fr;
  }
}
.od__col {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.od__card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.od__card-hd {
  padding: 1.125rem 1.5rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.od__card-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.od__info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  padding: 1.5rem;
}
.od__cell {
  background: var(--nxt-surface-low);
  border-radius: 0.875rem;
  padding: 0.875rem 1rem;
}
.od__cell-label {
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  letter-spacing: 0.03em;
}
.od__cell-val {
  display: block;
  margin-top: 0.2rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}

.od__reject {
  margin: 0 1.5rem 1.5rem;
  background: rgba(186, 26, 26, 0.06);
  border: 1px solid rgba(186, 26, 26, 0.2);
  border-radius: 0.875rem;
  padding: 0.875rem 1rem;
}
.od__reject-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #dc2626;
  margin: 0 0 0.25rem;
}
.od__reject-msg {
  font-size: 0.875rem;
  color: #991b1b;
  margin: 0;
}

.od__client {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
}
.od__client-avatar {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-variant);
  flex-shrink: 0;
}
.od__client-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  margin: 0;
}
.od__client-sub {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  margin: 0.1rem 0 0;
}

.od__note {
  margin: 0 1.5rem 1.5rem;
  background: rgba(255, 251, 235, 0.8);
  border: 1px solid #fde68a;
  border-radius: 0.875rem;
  padding: 0.875rem 1rem;
}
.od__note-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #b45309;
  margin: 0 0 0.25rem;
}
.od__note-text {
  font-size: 0.875rem;
  color: #92400e;
  font-style: italic;
  margin: 0;
}

.od__pay-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 1.25rem 1.5rem;
}
.od__pay-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: var(--nxt-surface-low);
  border-radius: 0.875rem;
}
.od__pay-chip {
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 0.35rem;
}
.pay--green {
  background: rgba(16, 185, 129, 0.15);
  color: #047857;
}
.pay--amber {
  background: rgba(245, 158, 11, 0.15);
  color: #b45309;
}
.od__pay-meta {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  margin-left: 0.5rem;
}
.od__pay-cur {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  margin: 0.25rem 0 0;
}
.od__pay-amount {
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.od__totals {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 1.25rem 1.5rem;
}
.od__total-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}
.od__total-row span:last-child {
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.od__discount {
  color: #16a34a !important;
}
.od__total-row--final {
  padding-top: 0.75rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.od__total-row--final span {
  font-size: 1rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}

.od__items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
}
.od-item {
  display: flex;
  gap: 0.875rem;
  align-items: flex-start;
  background: var(--nxt-surface-low);
  border-radius: 0.875rem;
  padding: 0.875rem;
}
.od-item__img {
  width: 3.5rem;
  height: 3.5rem;
  flex-shrink: 0;
  border-radius: 0.625rem;
  background: var(--nxt-surface-mid);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-muted);
}
.od-item__img img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.od-item__body {
  flex: 1;
  min-width: 0;
}
.od-item__name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  margin: 0;
}
.od-item__cat {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted);
  margin: 0.125rem 0 0;
}
.od-item__qty {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  margin: 0.2rem 0 0;
}
.od-item__note {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted);
  font-style: italic;
  margin: 0.25rem 0 0;
}
.od-item__total {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  flex-shrink: 0;
}
.od-item__extras {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.od-item__extra-title {
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 0.25rem;
}
.od-item__extra-opts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.od-item__opt {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface);
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.375rem;
  padding: 0.15rem 0.5rem;
}
.od-item__opt-price {
  color: var(--nxt-on-surface);
  font-weight: 700;
}

.od__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
}
.od__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
</style>

<style>
[data-theme='dark'] .od__header,
[data-theme='dark'] .od__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .od__back,
[data-theme='dark'] .od__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .od__cell,
[data-theme='dark'] .od__pay-row,
[data-theme='dark'] .od-item {
  background: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .od-item__img {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .od-item__opt {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
}
</style>
